import { useEffect, useState } from 'react';
import { Plus, X, FolderKanban, Search } from 'lucide-react';
import { useProjectStore } from '../../stores/projectStore';
import { AGENTS, DEPARTMENTS } from '../../utils/agents';
import Spinner from '../common/Spinner';
import EmptyState from '../common/EmptyState';
import ProjectCard from './ProjectCard';
import ProjectDetail from './ProjectDetail';
import CreateProjectModal from './CreateProjectModal';

const STATUS_OPTIONS = [
  { key: '', label: 'All Statuses' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'archived', label: 'Archived' },
];

export default function ProjectsPage() {
  const projects = useProjectStore((s) => s.projects);
  const loading = useProjectStore((s) => s.loading);
  const filters = useProjectStore((s) => s.filters);
  const setFilters = useProjectStore((s) => s.setFilters);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);

  const [showCreate, setShowCreate] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const hasFilters = filters.status || filters.department || filters.lead_agent || filters.search;

  function clearFilters() {
    setSearchInput('');
    setFilters({ status: undefined, department: undefined, lead_agent: undefined, search: undefined });
  }

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      setFilters({ search: searchInput.trim() || undefined });
    }
  }

  function handleSearchBlur() {
    setFilters({ search: searchInput.trim() || undefined });
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Projects</h1>
        {loading ? <Spinner size="sm" /> : null}
      </div>

      {/* Filter bar */}
      <div className="bg-bg-secondary rounded-lg p-3 flex flex-row gap-3 items-center flex-wrap">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onBlur={handleSearchBlur}
            placeholder="Search projects..."
            className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md pl-8 pr-3 py-1.5 text-sm w-full sm:w-48 focus:outline-none focus:border-accent-purple"
          />
        </div>

        <select
          value={filters.status || ''}
          onChange={(e) => setFilters({ status: e.target.value || undefined })}
          className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          value={filters.department || ''}
          onChange={(e) => setFilters({ department: e.target.value || undefined })}
          className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">All Departments</option>
          {DEPARTMENTS.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>

        <select
          value={filters.lead_agent || ''}
          onChange={(e) => setFilters({ lead_agent: e.target.value || undefined })}
          className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">All Agents</option>
          {AGENTS.map((a) => (
            <option key={a.slug} value={a.slug}>
              {a.name}
            </option>
          ))}
        </select>

        {hasFilters ? (
          <button
            onClick={clearFilters}
            className="text-text-muted hover:text-text-secondary text-sm flex items-center gap-1 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        ) : null}

        <div className="flex-1" />

        <button
          onClick={() => setShowCreate(true)}
          className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Project
        </button>
      </div>

      {/* Project grid */}
      {!loading && projects.length === 0 ? (
        <EmptyState
          icon={<FolderKanban />}
          title="No projects yet"
          description={hasFilters ? 'Try adjusting your filters.' : 'Create your first project to get started.'}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => setSelectedProjectId(project.id)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate ? (
        <CreateProjectModal onClose={() => setShowCreate(false)} />
      ) : null}

      {selectedProjectId !== null ? (
        <ProjectDetail
          projectId={selectedProjectId}
          onClose={() => setSelectedProjectId(null)}
        />
      ) : null}
    </div>
  );
}
